import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaCheckCircle, FaCloudUploadAlt } from "react-icons/fa";
import { Trash2, Edit, Search } from "lucide-react";
import axios from "axios";
import Apiurl from "../environment/Apiurl";

const HospitalMaintenance = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [maintenances, setMaintenances] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    maintenance_id: "",
    maintenance_Date: "",
    department: "",
    maintenance_type: "",
    area_name: "",
    vendor: "",
    amount: "",
    added_by: "",
    Status: "",
    file: null,
  });

  const totalPages = Math.ceil(maintenances.length / itemsPerPage) || 1;

  // Fetch maintenance data
  const fetchData = async () => {
    try {
      const response = await axios.get(`${Apiurl}/maintenance`);
      setMaintenances(response.data);
    } catch (error) {
      setError("Failed to fetch maintenance data.");
    }
  };

  // ✅ Generate new Maintenance ID
  const HospitalGenerateId = async () => {
    try {
      const res = await axios.get(`${Apiurl}/hospital_maintenance_id`);
      if (res.data?.maintenance_id) {
        setFormData((prev) => ({
          ...prev,
          maintenance_id: res.data.maintenance_id,
        }));
      }
    } catch (error) {
      console.error("Error generating maintenance ID:", error.message);
    }
  };

  useEffect(() => {
    fetchData();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Pagination logic with ellipsis
  const getPageList = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 4) {
      pages.push(1, 2, 3, 4, 5, "...", totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(
        1,
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages
      );
    } else {
      pages.push(
        1,
        "...",
        currentPage - 1,
        currentPage,
        currentPage + 1,
        "...",
        totalPages
      );
    }
    return pages;
  };

  const handlePageClick = (page) => {
    if (page !== "..." && page !== currentPage) setCurrentPage(page);
  };

  const filteredMaintenances = maintenances.filter(
    (m) =>
      m.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.maintenance_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.area_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.vendor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.added_by?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const currentItems = filteredMaintenances.slice(
    indexOfLastItem - itemsPerPage,
    indexOfLastItem
  );

  // ✅ Open Add Modal & Generate New ID
  const openModal = async () => {
    setIsEditMode(false);
    setFormData({
      maintenance_id: "",
      maintenance_Date: "",
      department: "",
      maintenance_type: "",
      area_name: "",
      vendor: "",
      amount: "",
      added_by: "",
      Status: "",
      file: null,
    });
    setError(null);
    setShowModal(true);
    await HospitalGenerateId(); // 🔹 Generate ID when Add Modal opens
  };

  const closeModal = () => {
    setShowModal(false);
    setIsEditMode(false);
    setEditId(null);
    setError(null);
  };

  const openEditModal = (maintenance) => {
    setIsEditMode(true);
    setEditId(maintenance._id);
    setFormData({
      maintenance_id: maintenance.maintenance_id || "",
      maintenance_Date: maintenance.maintenance_Date.split("T")[0] || "",
      department: maintenance.department || "",
      maintenance_type: maintenance.maintenance_type || "",
      area_name: maintenance.area_name || "",
      vendor: maintenance.vendor || "",
      amount: maintenance.amount || "",
      added_by: maintenance.added_by || "",
      Status: maintenance.Status || "",
      file: null,
    });
    setError(null);
    setShowModal(true);
  };

  const openDeleteModal = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => setShowDeleteModal(false);

  const handleFormChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "file" && files[0]) {
      const file = files[0];
      const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
      if (!allowedTypes.includes(file.type)) {
        setError("Invalid file type. Only JPEG, PNG, and PDF are allowed.");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError("File size exceeds 5MB limit.");
        return;
      }
    }
    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);

    try {
      const data = new FormData();
      for (const key in formData) {
        if (formData[key] !== null && formData[key] !== "") {
          data.append(key, formData[key]);
        }
      }

      if (isEditMode) {
        await axios.put(`${Apiurl}/maintenance/${editId}`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await axios.post(`${Apiurl}/maintenance`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      await fetchData();
      closeModal();
    } catch (error) {
      setError(
        error.response?.data?.error || "Failed to submit maintenance record."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${Apiurl}/maintenance/${deleteId}`);
      await fetchData();
      closeDeleteModal();
    } catch (error) {
      setError("Failed to delete maintenance record.");
    }
  };

  const pages = getPageList();

  return (
    <div className="p-3">
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      <div className="text-end mb-3">
        <button
          className="btn btn-sm text-white"
          style={{ backgroundColor: "#01AEBE" }}
          onClick={openModal}
        >
          + Add Maintenance
        </button>
      </div>

      <div className="card card-body">
        {/* Search + Show Count */}
        <div className="row mb-2 crm-table-toolbar">
          <div className="col-md-2">
            <div className="input-group">
              <span className="input-group-text">
                <i className="fas fa-table"></i>
              </span>
              <select
                className="form-select"
                onChange={(e) => {
                  setItemsPerPage(
                    e.target.value === "All"
                      ? filteredMaintenances.length
                      : Number(e.target.value)
                  );
                  setCurrentPage(1);
                }}
              >
                <option value="All">Show All</option>
                {[5, 10, 20, 50, 100, 200, 500, 1000].map((n) => (
                  <option key={n} value={n}>
                    Show {n}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="col-md-7"></div>

          <div className="col-md-3">
            <div className="position-relative">
              <input
                type="text"
                className="form-control pe-5"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <Search
                size={16}
                className="position-absolute text-muted"
                style={{
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="table-responsive">
          <table className="table">
            <thead className="table-light">
              <tr style={{ backgroundColor: "#01AEBE", color: "white" }}>
                <th>S.No</th>
                <th>Maint. ID</th>
                <th>Date</th>
                <th>Department</th>
                <th>Type</th>
                <th>Area Name</th>
                <th>Vendor</th>
                <th>₹ Amount</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.length > 0 ? (
                currentItems.map((maintenance, index) => (
                  <tr key={maintenance._id} className="align-middle">
                    <td>{index + 1 + (currentPage - 1) * itemsPerPage}</td>
                    <td>{maintenance.maintenance_id}</td>
                    <td>
                      {new Date(
                        maintenance.maintenance_Date
                      ).toLocaleDateString()}
                    </td>
                    <td>{maintenance.department}</td>
                    <td>{maintenance.maintenance_type}</td>
                    <td>{maintenance.area_name}</td>
                    <td>{maintenance.vendor}</td>
                    <td>₹{maintenance.amount}</td>
                    <td>
                      <span
                        className={`badge rounded-pill bg-${
                          maintenance.Status === "Completed"
                            ? "success"
                            : "warning"
                        }-subtle text-${
                          maintenance.Status === "Completed"
                            ? "success"
                            : "warning"
                        } fw-semibold px-3 py-2`}
                      >
                        <FaCheckCircle /> {maintenance.Status}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm p-1 border-0"
                        onClick={() => openEditModal(maintenance)}
                      >
                        <Edit size={16} className="text-primary" />
                      </button>
                      <button
                        className="btn btn-sm p-1 border-0"
                        onClick={() => openDeleteModal(maintenance._id)}
                      >
                        <Trash2 size={16} className="text-danger" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="text-center text-muted py-3">
                    No maintenance records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex justify-content-between align-items-center p-3 border-top gap-2">
          <button
            className="btn btn-outline-secondary"
            onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>

          <div className="d-flex gap-2 overflow-auto flex-nowrap px-2">
            {getPageList().map((page, i) => (
              <button
                key={i}
                className={`btn btn-sm ${
                  page === currentPage ? "text-white" : "btn-outline-secondary"
                }`}
                style={
                  page === currentPage
                    ? { backgroundColor: "#20c997", border: "none" }
                    : {}
                }
                onClick={() => handlePageClick(page)}
                disabled={page === "..."}
              >
                {page}
              </button>
            ))}
          </div>

          <button
            className="btn btn-outline-secondary"
            onClick={() =>
              currentPage < totalPages && setCurrentPage(currentPage + 1)
            }
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {/* ✅ Add/Edit Modal */}
      {showModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h6 className="modal-title">
                    {isEditMode ? "Edit Maintenance" : "Add Maintenance"}
                  </h6>
                  <button className="btn-close" onClick={closeModal}></button>
                </div>
                <div className="modal-body">
                  {error && (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  )}
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-md-6 mb-2">
                        <label>Maintenance ID</label>
                        <input
                          type="text"
                          name="maintenance_id"
                          className="form-control"
                          value={formData.maintenance_id}
                          readOnly
                          required
                        />
                      </div>
                      <div className="col-md-6 mb-2">
                        <label>Maintenance Date</label>
                        <input
                          type="date"
                          name="maintenance_Date"
                          className="form-control"
                          value={formData.maintenance_Date}
                          onChange={handleFormChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-2">
                        <label>Department</label>
                        <input
                          type="text"
                          name="department"
                          className="form-control"
                          value={formData.department}
                          onChange={handleFormChange}
                          required
                        />
                      </div>
                      <div className="col-md-6 mb-2">
                        <label>Maintenance Type</label>
                        <input
                          type="text"
                          name="maintenance_type"
                          className="form-control"
                          value={formData.maintenance_type}
                          onChange={handleFormChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-2">
                        <label>Area Name</label>
                        <input
                          type="text"
                          name="area_name"
                          className="form-control"
                          value={formData.area_name}
                          onChange={handleFormChange}
                          required
                        />
                      </div>
                      <div className="col-md-6 mb-2">
                        <label>Vendor</label>
                        <input
                          type="text"
                          name="vendor"
                          className="form-control"
                          value={formData.vendor}
                          onChange={handleFormChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="mb-2">
                      <label>Amount (₹)</label>
                      <input
                        type="number"
                        name="amount"
                        className="form-control"
                        value={formData.amount}
                        onChange={handleFormChange}
                        required
                      />
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-2">
                        <label>Added By</label>
                        <input
                          type="text"
                          name="added_by"
                          className="form-control"
                          value={formData.added_by}
                          onChange={handleFormChange}
                          required
                        />
                      </div>
                      <div className="col-md-6 mb-2">
                        <label>Status</label>
                        <select
                          name="Status"
                          className="form-control"
                          value={formData.Status}
                          onChange={handleFormChange}
                          required
                        >
                          <option value="">Select Status</option>
                          <option value="Completed">Completed</option>
                          <option value="Ongoing">Ongoing</option>
                        </select>
                      </div>
                    </div>

                    <div className="mb-4 text-center">
                      <h6>Upload Document (JPEG, PNG, PDF)</h6>
                      <label
                        htmlFor="file-upload"
                        className="d-block border rounded p-3 cursor-pointer"
                      >
                        <FaCloudUploadAlt size={30} className="mb-2" />
                        <p>
                          {formData.file
                            ? formData.file.name
                            : "Click to choose file"}
                        </p>
                        <input
                          id="file-upload"
                          type="file"
                          name="file"
                          className="d-none"
                          accept="image/jpeg,image/png,application/pdf"
                          onChange={handleFormChange}
                        />
                      </label>
                    </div>

                    <div className="text-center">
                      <button
                        type="submit"
                        className="btn text-white px-5"
                        style={{ backgroundColor: "#01AEBE" }}
                        disabled={submitting}
                      >
                        {submitting
                          ? "Saving..."
                          : isEditMode
                          ? "Update"
                          : "Confirm"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-sm modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header border-0">
                  <button
                    className="btn-close"
                    onClick={closeDeleteModal}
                  ></button>
                </div>
                <div className="modal-body text-center">
                  <div
                    className="mx-auto mb-3"
                    style={{
                      width: "50px",
                      height: "50px",
                      backgroundColor: "#fee2e2",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <i
                      className="fas fa-exclamation-triangle text-danger fs-4"
                      aria-hidden="true"
                    ></i>
                  </div>
                  <h6>Delete Maintenance</h6>
                  <p>Are you sure you want to delete this record?</p>
                </div>
                <div className="modal-footer border-0 justify-content-center">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={closeDeleteModal}
                  >
                    No, Keep it
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={handleDelete}
                  >
                    Yes, Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default HospitalMaintenance;
